import { Alert, AlertTitle, Box } from '@mui/material';
import type { FieldErrors } from 'react-hook-form';

interface Props {
  /** react-hook-form's `formState.errors`. */
  errors?: FieldErrors;
  /** Extra global messages (e.g. server errors not matched to a field). */
  messages?: string[];
  /** Heading text. */
  title?: string;
}

/** Collects human messages from RHF's (possibly nested) error tree. */
function collectMessages(errors?: FieldErrors): string[] {
  if (!errors) return [];
  const out: string[] = [];
  const walk = (node: unknown) => {
    if (!node || typeof node !== 'object') return;
    const rec = node as Record<string, unknown>;
    if (typeof rec.message === 'string' && rec.message) out.push(rec.message);
    for (const key of Object.keys(rec)) {
      if (key === 'message' || key === 'type' || key === 'ref') continue;
      walk(rec[key]);
    }
  };
  walk(errors);
  return out;
}

/**
 * Top-of-form error summary (Phase 12). When one or more fields are invalid it shows a single calm
 * banner prompting the user to fix the highlighted fields, then lists each issue. The inline field
 * errors still render beneath each field; this is the at-a-glance summary. Renders nothing when valid.
 */
export default function FormErrorSummary({
  errors,
  messages = [],
  title = 'Please correct the highlighted fields.',
}: Props) {
  const all = [...new Set([...collectMessages(errors), ...messages])];
  if (all.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Alert severity="error" variant="outlined" sx={{ py: 0.5, '& .MuiAlert-message': { minWidth: 0 } }}>
        <AlertTitle sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: all.length > 1 ? 0.5 : 0.25 }}>{title}</AlertTitle>
        <Box component="ul" sx={{ m: 0, pl: 2.25, fontSize: '0.8125rem', '& li + li': { mt: 0.25 } }}>
          {all.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </Box>
      </Alert>
    </Box>
  );
}
